import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { X, LogOut, AlertTriangle } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

const SignOutModal = ({ isOpen, onClose, onConfirm }) => {
    const { t } = useTranslation()
    const modalRef = useRef(null)

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose()
            }
        }

        // Close modal on escape key press
        const handleEscKey = (event) => {
            if (event.key === "Escape") {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside)
            document.addEventListener("keydown", handleEscKey)
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("keydown", handleEscKey)
        }
    }, [isOpen, onClose])

    // Animation variants
    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.2 },
        },
    }

    const modalVariants = {
        hidden: {
            opacity: 0,
            scale: 0.9,
            y: 10,
            transition: {
                duration: 0.15,
                ease: "easeIn",
            },
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut",
                delay: 0.05,
            },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            transition: {
                duration: 0.15,
                ease: "easeIn",
            },
        },
    }

    const buttonVariants = {
        hover: { scale: 1.03 },
        tap: { scale: 0.97 },
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                >
                    <motion.div
                        ref={modalRef}
                        className="w-full text-start max-w-md rounded-xl shadow-lg bg-white dark:border-neutral-900 dark:bg-neutral-900 text-neutral-800 dark:text-white overflow-hidden border-t-4 border-t-neutral-400 dark:border-t-neutral-600"
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        {/* Modal header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-150 dark:border-neutral-800">
                            <div className="flex items-center gap-1">
                                <div className=" p-2 rounded-full text-neutral-600 dark:text-neutral-400">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                </div>
                                <h3 className="text-md font-[400]">{t("Sign Out Confirmation")}</h3>
                            </div>
                            <motion.button
                                onClick={onClose}
                                className="p-1 rounded-full text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <X size={18} />
                            </motion.button>
                        </div>

                        {/* Modal content */}
                        <div className="px-6 py-4">
                            <motion.p
                                className="text-neutral-600 font-[300] text-sm dark:text-neutral-300"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.2 }}
                            >
                                {t("Are you sure you want to sign out? Any unsaved changes will be lost.")}
                            </motion.p>
                        </div>

                        {/* Modal footer */}
                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-150 dark:border-neutral-800">
                            <motion.button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium rounded-xl bg-white dark:bg-neutral-700 text-neutral-700 dark:text-white hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors border border-neutral-300 dark:border-neutral-600"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                {t("Cancel")}
                            </motion.button>
                            <motion.button
                                onClick={onConfirm}
                                className="px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors flex items-center bg-red-600 hover:bg-red-700 dark:hover:bg-red-700"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <LogOut size={16} className="mr-2" />
                                {t("Sign Out")}
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default SignOutModal

